import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  colors,
  DialogActions,
  DialogContent,
  Divider,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  bulkUpdateConsumableDefaultRates,
  compareConsumableDefaultRatesImport,
  compareConsumableDefaultRatesUpdateImport,
  downloadConsumableDefaultRatesNewTemplate,
  downloadConsumableDefaultRatesUpdateTemplate,
  listConsumableItems,
  BulkUpdateDefaultRatesResponse,
} from 'api/consumablesApi';
import { handleMutationError } from 'util/handleMutationError';
import BulkUpdateDefaultRatesForm from './BulkUpdateDefaultRatesForm';
import BulkUpdateDefaultRatesResultView from './BulkUpdateDefaultRatesResultView';
import { TabProps } from './ScopedRateSharedTab';

const DefaultRatesTab = ({ mode, onBusyChange, onClose }: TabProps) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BulkUpdateDefaultRatesResponse | null>(
    null
  );
  const [result, setResult] = useState<BulkUpdateDefaultRatesResponse | null>(
    null
  );

  const { data: consumableData } = useQuery(
    ['consumables-items'],
    listConsumableItems
  );
  const noExistingData = (consumableData?.length ?? 0) === 0;

  const { mutate: previewMutate, isLoading: isPreviewLoading } = useMutation(
    mode === 'update'
      ? compareConsumableDefaultRatesUpdateImport
      : compareConsumableDefaultRatesImport,
    {
      onSuccess: (data) => setPreview(data),
      onError: (error) => {
        enqueueSnackbar(
          `Failed to read the file: ${handleMutationError(error)}`,
          { variant: 'error' }
        );
      },
    }
  );

  const { mutate: commitMutate, isLoading: isCommitLoading } = useMutation(
    bulkUpdateConsumableDefaultRates,
    {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(['consumables-items']);
        setResult(data);
        enqueueSnackbar(
          data.errorCount === 0
            ? 'Consumable default rates updated successfully'
            : `Consumable default rates updated — ${data.errorCount} row(s) skipped`,
          { variant: 'success' }
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          `Failed to update consumable default rates: ${handleMutationError(error)}`,
          { variant: 'error' }
        );
      },
    }
  );

  const { mutate: downloadMutate, isLoading: isDownloadingTemplate } = useMutation(
    mode === 'update'
      ? downloadConsumableDefaultRatesUpdateTemplate
      : downloadConsumableDefaultRatesNewTemplate,
    {
      onError: (error) => {
        enqueueSnackbar(
          `Failed to download the template: ${handleMutationError(error)}`,
          { variant: 'error' }
        );
      },
    }
  );

  const isBusy = isPreviewLoading || isCommitLoading || isDownloadingTemplate;
  React.useEffect(() => onBusyChange(isBusy), [isBusy, onBusyChange]);

  const step = result ? 'result' : preview ? 'preview' : 'form';

  const resultDivergedFromPreview =
    !!preview &&
    !!result &&
    (preview.createdCount !== result.createdCount ||
      preview.updatedCount !== result.updatedCount ||
      preview.ignoredCount !== result.ignoredCount ||
      preview.errorCount !== result.errorCount);

  return (
    <>
      {step === 'form' && (
        <>
          <DialogContent sx={{ p: 0 }}>
            <BulkUpdateDefaultRatesForm
              file={file}
              setFile={setFile}
              isLoading={isPreviewLoading}
              mode={mode}
              noExistingData={noExistingData}
              onDownloadTemplate={() => downloadMutate()}
              isDownloadingTemplate={isDownloadingTemplate}
            />
          </DialogContent>
          <Divider />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button
                onClick={onClose}
                sx={{ color: colors.grey[600], mr: 1 }}
                disabled={isPreviewLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isPreviewLoading || !file}
                endIcon={
                  isPreviewLoading ? (
                    <CircularProgress size={15} />
                  ) : (
                    <ArrowForwardIcon />
                  )
                }
                onClick={() => {
                  if (!file) return;
                  previewMutate({ file });
                }}>
                {isPreviewLoading ? 'Please wait...' : 'Preview'}
              </Button>
            </Box>
          </DialogActions>
        </>
      )}

      {step === 'preview' && preview && (
        <>
          <BulkUpdateDefaultRatesResultView response={preview} />
          <Divider />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button
                onClick={() => setPreview(null)}
                sx={{ color: colors.grey[600], mr: 1 }}
                disabled={isCommitLoading}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={
                  isCommitLoading ||
                  !preview.previewToken ||
                  (preview.createdCount === 0 && preview.updatedCount === 0)
                }
                endIcon={
                  isCommitLoading ? <CircularProgress size={15} /> : <CheckIcon />
                }
                onClick={() => {
                  if (!preview.previewToken) return;
                  commitMutate({ previewToken: preview.previewToken });
                }}>
                {isCommitLoading
                  ? 'Please wait...'
                  : preview.errorCount > 0
                    ? 'Ignore Errors and Continue'
                    : 'Confirm Import'}
              </Button>
            </Box>
          </DialogActions>
        </>
      )}

      {step === 'result' && result && (
        <>
          {resultDivergedFromPreview && (
            <Alert severity="warning" sx={{ m: 2 }}>
              The committed result differs from the preview you approved,
              data may have changed, or effective dates rolled over since
              preview. Review the details below.
            </Alert>
          )}
          <BulkUpdateDefaultRatesResultView response={result} />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button
                variant="contained"
                color="primary"
                endIcon={<CheckIcon />}
                onClick={onClose}>
                Done
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </>
  );
};

export default DefaultRatesTab;
